import { Copy, Plus, Trash2, Upload, X } from 'lucide-react';
import { useState } from 'react';
import type { Node } from 'reactflow';
import { useQuery } from '@tanstack/react-query';
import { listDepartments, listUsers } from '@/features/configuracoes/api';
import type { MediaType } from '@/features/media-library/api';
import { MediaPicker } from '@/features/media-library/MediaPicker';
import {
  NODE_STYLES,
  type MenuOption,
  type NodeKind,
  type ScheduleWindow,
  type VarBranch,
  type VarComparator,
  VAR_COMPARATOR_LABEL,
  summarizeScheduleWindow,
} from './node-types';

interface Props {
  node: Node | null;
  onChange: (config: Record<string, unknown>) => void;
  onLabelChange: (label: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export function SidePanel({
  node,
  onChange,
  onLabelChange,
  onDuplicate,
  onDelete,
  onClose,
}: Props) {
  if (!node) return null;
  const kind = (node.data?.kind ?? node.type) as NodeKind;
  const cfg = (node.data?.config ?? {}) as Record<string, unknown>;
  const label = (node.data?.label as string | undefined) ?? '';
  const style = NODE_STYLES[kind];

  return (
    <aside className="flex h-full w-[340px] flex-col border-n-200 border-l bg-n-0">
      <header className="flex items-center justify-between border-n-200 border-b px-4 py-3">
        <div>
          <h3 className="font-bold text-[14px] text-n-900">Propriedades · {style.label}</h3>
          <span className="text-[11px] text-n-500">
            Nó selecionado: <b className="text-brand-700">{label || style.label}</b>
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-n-500 hover:text-n-700"
          aria-label="Fechar"
        >
          ×
        </button>
      </header>
      <div className="flex-1 space-y-4 overflow-y-auto p-4 text-[12px]">
        <Field label="Nome do nó (opcional)">
          <input
            value={label}
            onChange={(e) => onLabelChange(e.target.value)}
            placeholder="Ex: Mensagem boas-vindas"
            className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
          />
        </Field>

        {kind === 'trigger' && <TriggerEditor cfg={cfg} onChange={onChange} />}
        {kind === 'message' && <MessageEditor cfg={cfg} onChange={onChange} />}
        {kind === 'input' && <InputEditor cfg={cfg} onChange={onChange} />}
        {kind === 'condition' && <ConditionEditor cfg={cfg} onChange={onChange} />}
        {kind === 'condition_vars' && <ConditionVarsEditor cfg={cfg} onChange={onChange} />}
        {kind === 'menu' && <MenuEditor cfg={cfg} onChange={onChange} />}
        {kind === 'schedule_table' && <ScheduleTableEditor cfg={cfg} onChange={onChange} />}
        {kind === 'transfer_human' && <TransferEditor cfg={cfg} onChange={onChange} />}
        {kind === 'wait' && <WaitEditor cfg={cfg} onChange={onChange} />}
        {kind === 'webhook' && <WebhookEditor cfg={cfg} onChange={onChange} />}
        {kind === 'end' && <EndEditor cfg={cfg} onChange={onChange} />}
      </div>

      <footer className="flex flex-col gap-2 border-n-100 border-t bg-n-50 px-4 py-3">
        {kind !== 'trigger' && (
          <>
            <button
              type="button"
              onClick={onDuplicate}
              className="flex items-center justify-center gap-2 rounded border border-n-200 bg-n-0 px-3 py-2 font-bold text-[11px] text-n-700 uppercase tracking-[.05em] hover:bg-n-50"
            >
              <Copy size={13} strokeWidth={1.75} />
              Duplicar nó
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center justify-center gap-2 rounded border border-danger-200 bg-n-0 px-3 py-2 font-bold text-[11px] text-danger-600 uppercase tracking-[.05em] hover:bg-danger-50"
            >
              <Trash2 size={13} strokeWidth={1.75} />
              Excluir nó
            </button>
          </>
        )}
        {kind === 'trigger' && (
          <p className="text-center text-[11px] text-n-500">
            O nó <b>Início</b> não pode ser duplicado nem excluído.
          </p>
        )}
      </footer>
    </aside>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block font-semibold text-[11px] text-n-600 uppercase tracking-[.04em]">
        {label}
      </span>
      {children}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
    />
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded border border-n-200 bg-n-0 px-2 py-2 text-[12px] focus:border-brand-500 focus:outline-none"
    />
  );
}

/**
 * Input em "tag pills": user digita palavra + Enter (ou vírgula, ou Tab) e ela
 * vira chip removível. Backspace com input vazio remove a última. Suporta
 * paste de lista separada por vírgula.
 */
function KeywordsInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');

  const commit = (raw: string): void => {
    // Suporta múltiplos itens via vírgula no paste/digitação rápida.
    const parts = raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    const next = Array.from(new Set([...value, ...parts]));
    onChange(next);
    setDraft('');
  };

  const removeAt = (idx: number): void => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex min-h-[36px] flex-wrap items-center gap-1.5 rounded border border-n-200 bg-n-0 px-2 py-1.5 focus-within:border-brand-500">
      {value.map((kw, i) => (
        <span
          key={`${kw}-${i}`}
          className="inline-flex items-center gap-1 rounded-pill bg-brand-100 px-2 py-0.5 font-semibold text-[11px] text-brand-700"
        >
          {kw}
          <button
            type="button"
            onClick={() => removeAt(i)}
            aria-label={`Remover ${kw}`}
            className="-mr-1 ml-0.5 rounded-pill p-0.5 hover:bg-brand-200/60"
          >
            <X size={11} strokeWidth={2.5} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          // Vírgula confirma a palavra atual sem precisar Enter.
          if (v.includes(',')) {
            commit(v);
            return;
          }
          setDraft(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Tab') {
            if (draft.trim().length > 0) {
              e.preventDefault();
              commit(draft);
            }
          } else if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
            e.preventDefault();
            removeAt(value.length - 1);
          }
        }}
        onBlur={() => {
          if (draft.trim().length > 0) commit(draft);
        }}
        placeholder={value.length === 0 ? (placeholder ?? 'preço, valor, quanto') : ''}
        className="min-w-[80px] flex-1 bg-transparent text-[12px] outline-none placeholder:text-n-400"
      />
    </div>
  );
}

function TriggerEditor({
  cfg,
  onChange,
}: { cfg: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const triggerType = (cfg.trigger_type as string | undefined) ?? 'any_message';
  const keywords = (cfg.keywords as string[] | undefined) ?? [];

  return (
    <>
      <Field label="Tipo de gatilho">
        <select
          value={triggerType}
          onChange={(e) => onChange({ ...cfg, trigger_type: e.target.value })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="any_message">Qualquer mensagem</option>
          <option value="keyword">Palavra-chave</option>
          <option value="first_contact">Primeiro contato</option>
          <option value="manual">Manual (broadcast)</option>
        </select>
      </Field>
      {triggerType === 'keyword' && (
        <Field label="Palavras-chave">
          <KeywordsInput
            value={keywords}
            onChange={(next) => onChange({ ...cfg, keywords: next })}
          />
        </Field>
      )}
      <p className="rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        💡 <b>any_message:</b> dispara em todo inbound (caro). <b>keyword:</b> só quando texto
        contém alguma palavra. <b>first_contact:</b> só no primeiro inbound histórico.
      </p>
    </>
  );
}

function InputEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const inputType = (cfg.input_type as string | undefined) ?? 'text';
  const variable = (cfg.variable as string | undefined) ?? '{{resposta}}';
  const prompt = (cfg.prompt as string | undefined) ?? '';
  const timeout = (cfg.timeout_minutes as number | undefined) ?? 5;
  const onTimeout = (cfg.on_timeout as string | undefined) ?? 'end';

  return (
    <>
      <Field label="Tipo de entrada">
        <select
          value={inputType}
          onChange={(e) => onChange({ ...cfg, input_type: e.target.value })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="text">Texto livre</option>
          <option value="number">Número</option>
          <option value="email">E-mail</option>
          <option value="buttons">Botões (escolha rápida)</option>
          <option value="list">Lista (menu)</option>
        </select>
      </Field>
      <Field label="Variável (onde salvar)">
        <TextInput
          value={variable}
          onChange={(v) => onChange({ ...cfg, variable: v })}
          placeholder="{{nome}}"
        />
      </Field>
      <Field label="Mensagem antes da captura">
        <TextArea
          value={prompt}
          onChange={(v) => onChange({ ...cfg, prompt: v })}
          placeholder="Pra te atender melhor, qual é o seu nome?"
          rows={3}
        />
      </Field>
      <Field label="Timeout (minutos)">
        <input
          type="number"
          min={1}
          max={1440}
          value={timeout}
          onChange={(e) => onChange({ ...cfg, timeout_minutes: Number(e.target.value) })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        />
      </Field>
      <Field label="Se timeout">
        <select
          value={onTimeout}
          onChange={(e) => onChange({ ...cfg, on_timeout: e.target.value })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="end">Encerrar fluxo</option>
          <option value="repeat">Repetir mensagem</option>
          <option value="transfer_human">Transferir pra humano</option>
        </select>
      </Field>
      <p className="rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        💡 A resposta do contato fica em <code className="font-mono">state.{`<variável>`}</code> e
        pode ser usada nos próximos nós com <code className="font-mono">{'{{state.X}}'}</code>.
      </p>
    </>
  );
}

function EndEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const closeTicket = (cfg.close_ticket as boolean | undefined) ?? false;
  return (
    <>
      <Field label="Comportamento">
        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            checked={closeTicket}
            onChange={(e) => onChange({ ...cfg, close_ticket: e.target.checked })}
            className="mt-1 h-4 w-4"
          />
          <span className="text-[12px] text-n-700">
            <b>Fechar ticket</b> ao encerrar (default: mantém aberto pra atendente humano).
          </span>
        </label>
      </Field>
      <p className="rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        O nó <b>Fim</b> marca a execução como concluída. O ticket fica como configurado acima — sem
        fechar por padrão (atendente humano pode continuar).
      </p>
    </>
  );
}

function MessageEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  // Modo: 'text' = mensagem de texto puro; 'media' = arquivo da galeria.
  // Tipo concreto (image/audio/document) é derivado do mime_type da mídia
  // selecionada — sem dropdown de tipo, simplifica UX.
  // Marcador explícito `cfg.mode` permite ficar em "media" mesmo SEM mídia
  // escolhida ainda (estado intermediário do user clicando "Enviar arquivo"
  // antes de pickar). Fallback: deriva de media_id pra compat com bots antigos.
  const explicitMode = cfg.mode as 'text' | 'media' | undefined;
  const isMedia = explicitMode === 'media' || (explicitMode == null && cfg.media_id != null);
  const mode = isMedia ? 'media' : 'text';
  const text = (cfg.text as string | undefined) ?? '';
  const mediaId = cfg.media_id as number | undefined;
  const mediaMime = cfg.media_mime as string | undefined;
  const originalName = cfg.original_name as string | undefined;
  const mediaLabel = cfg.media_label as string | undefined;

  // Tipo da mídia atual (se houver) — controla se mostra campo de legenda.
  const mediaType: MediaType | null = mediaMime
    ? mediaMime.startsWith('image/')
      ? 'image'
      : mediaMime.startsWith('audio/')
        ? 'audio'
        : mediaMime.startsWith('video/')
          ? 'video'
          : 'document'
    : null;

  const switchTo = (newMode: 'text' | 'media'): void => {
    if (newMode === mode) return;
    const { media_id, media_mime, media_label, original_name, mode: _m, text: txt, ...rest } = cfg;
    void media_id;
    void media_mime;
    void media_label;
    void original_name;
    void _m;
    if (newMode === 'text') {
      onChange({ ...rest, mode: 'text', message_type: 'text', ...(txt != null && { text: txt }) });
    } else {
      // Marca `mode='media'` mesmo sem mídia escolhida — render mostra
      // "Escolher da galeria". Quando user pickar, message_type vira
      // o tipo concreto (image/audio/etc). Mantém texto pra virar caption.
      const { message_type, ...rest2 } = rest;
      void message_type;
      onChange({ ...rest2, mode: 'media', ...(txt != null && { text: txt }) });
    }
  };

  return (
    <>
      <Field label="Tipo">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => switchTo('text')}
            className={`flex-1 rounded px-3 py-1.5 font-semibold text-[12px] transition ${
              mode === 'text' ? 'bg-brand-500 text-white' : 'bg-n-100 text-n-700 hover:bg-n-200'
            }`}
          >
            Texto
          </button>
          <button
            type="button"
            onClick={() => switchTo('media')}
            className={`flex-1 rounded px-3 py-1.5 font-semibold text-[12px] transition ${
              mode === 'media' ? 'bg-brand-500 text-white' : 'bg-n-100 text-n-700 hover:bg-n-200'
            }`}
          >
            Enviar arquivo
          </button>
        </div>
      </Field>

      {mode === 'text' ? (
        <Field label="Texto da mensagem">
          <TextArea
            value={text}
            onChange={(v) => onChange({ ...cfg, text: v, message_type: 'text' })}
            placeholder="Olá {{contact.display_name}}! Posso te ajudar?"
            rows={6}
          />
        </Field>
      ) : (
        <>
          <Field label="Arquivo da galeria">
            <MediaPicker
              selectedId={mediaId}
              selectedLabel={mediaLabel ?? originalName}
              selectedMime={mediaMime}
              onSelect={(m) =>
                onChange({
                  ...cfg,
                  media_id: m.id,
                  media_mime: m.mime_type,
                  media_label: m.label,
                  original_name: m.original_name,
                  message_type: m.type,
                })
              }
              onClear={() => {
                const { media_id, media_mime, media_label, original_name, message_type, ...rest } =
                  cfg;
                void media_id;
                void media_mime;
                void media_label;
                void original_name;
                void message_type;
                onChange(rest);
              }}
            />
          </Field>
          {mediaType && mediaType !== 'audio' && (
            <Field label="Legenda (opcional)">
              <TextArea
                value={text}
                onChange={(v) => onChange({ ...cfg, text: v })}
                placeholder="Texto que acompanha a mídia"
                rows={3}
              />
            </Field>
          )}
        </>
      )}

      <p className="rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        💡 Use <code className="font-mono">{'{{contact.display_name}}'}</code>,{' '}
        <code className="font-mono">{'{{state.X}}'}</code> pra interpolar variáveis.
      </p>
    </>
  );
}

type ConditionSource = 'message' | 'variable' | 'atendido_hoje';

/** Token que o executor resolve pra "sim"/"nao" — este contato já passou por um
 *  atendimento HOJE (dia do calendário, fuso do cliente), mesmo que nenhum
 *  atendente tenha respondido. Vide worker-chatbot/atendido-hoje.ts. */
const TOKEN_ATENDIDO_HOJE = '{{contact.ja_atendido_hoje}}';
type ConditionOp = 'contains' | 'equals' | 'not_equals' | 'empty' | 'not_empty';

/**
 * Gera expression no formato do `evalCondition` do executor a partir dos
 * campos estruturados. Aceita múltiplos valores (chips) — vira regex
 * alternation `(a|b|c)`. Mantém compat com bots antigos via cfg.expression.
 */
function buildExpression(
  source: ConditionSource,
  variable: string,
  op: ConditionOp,
  values: string[],
): string {
  const path = source === 'message' ? 'message.body' : `state.${variable.trim()}`;
  if (source === 'variable' && !variable.trim()) return '';
  const cleaned = values.map((v) => v.trim()).filter((v) => v.length > 0);
  const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  switch (op) {
    case 'contains':
      if (cleaned.length === 0) return '';
      return `${path} matches /(${cleaned.map(escapeRegex).join('|')})/i`;
    case 'equals':
      if (cleaned.length === 0) return '';
      // Equality case-insensitive com qualquer um dos valores via regex anchored.
      return `${path} matches /^(${cleaned.map(escapeRegex).join('|')})$/i`;
    case 'not_equals':
      if (cleaned.length === 0) return '';
      // Negative lookahead — evalCondition do executor passa pro RegExp nativo.
      return `${path} matches /^(?!(${cleaned.map(escapeRegex).join('|')})$).*/i`;
    case 'empty':
      return `${path} == ""`;
    case 'not_empty':
      return `${path} != ""`;
    default:
      return '';
  }
}

function ConditionEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const source = (cfg.condition_source as ConditionSource | undefined) ?? 'message';
  const variable = (cfg.condition_variable as string | undefined) ?? '';
  const op = (cfg.condition_op as ConditionOp | undefined) ?? 'contains';
  // Backwards-compat: se cfg.condition_values era string única (versão antiga),
  // converte pra array de 1 elemento.
  const rawValues = cfg.condition_values;
  const values: string[] = Array.isArray(rawValues)
    ? (rawValues as string[])
    : typeof rawValues === 'string' && rawValues
      ? [rawValues]
      : typeof cfg.condition_value === 'string' && cfg.condition_value
        ? [cfg.condition_value as string]
        : [];
  const legacyExpr = (cfg.expression as string | undefined) ?? '';
  const hasStructured = cfg.condition_source != null;

  const update = (
    changes: Partial<{
      condition_source: ConditionSource;
      condition_variable: string;
      condition_op: ConditionOp;
      condition_values: string[];
    }>,
  ): void => {
    const nextSource = changes.condition_source ?? source;
    const nextVar = changes.condition_variable ?? variable;
    const nextOp = changes.condition_op ?? op;
    const nextValues = changes.condition_values ?? values;
    const expression = buildExpression(nextSource, nextVar, nextOp, nextValues);
    onChange({
      ...cfg,
      condition_source: nextSource,
      condition_variable: nextVar,
      condition_op: nextOp,
      condition_values: nextValues,
      expression,
    });
  };

  const showValue = op === 'contains' || op === 'equals' || op === 'not_equals';

  return (
    <>
      {!hasStructured && legacyExpr && (
        <div className="rounded bg-warning-50 px-3 py-2 text-[11px] text-warning-700">
          Esse nó usa expressão antiga: <code className="font-mono">{legacyExpr}</code>. Os campos
          abaixo vão sobrescrever ela quando você editar.
        </div>
      )}

      <Field label="Comparar">
        <select
          value={source}
          onChange={(e) => update({ condition_source: e.target.value as ConditionSource })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="message">Texto da mensagem do contato</option>
          <option value="variable">Variável do fluxo</option>
        </select>
      </Field>

      {source === 'atendido_hoje' && (
        <div className="mb-3 rounded bg-brand-50 px-3 py-2 text-[11px] text-brand-700">
          Vale <strong>sim</strong> quando este contato já passou por um atendimento{' '}
          <strong>hoje neste canal</strong> — de 00h00 a 23h59, no fuso da empresa. Conta mesmo que nenhum
          atendente tenha respondido: basta ter passado pelo bot e o atendimento ter sido
          encerrado.
          <br />
          Monte um ramo <code className="font-mono">igual a</code>{' '}
          <code className="font-mono">sim</code> ligado ao "Transferir pra humano": quem já falou
          com a empresa hoje não repete o menu (nem a cobrança dele).
        </div>
      )}

      {source === 'variable' && (
        <Field label="Nome da variável">
          <TextInput
            value={variable}
            onChange={(v) => update({ condition_variable: v })}
            placeholder="resposta, nome, qualified..."
          />
        </Field>
      )}

      <Field label="Condição">
        <select
          value={op}
          onChange={(e) => update({ condition_op: e.target.value as ConditionOp })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="contains">contém</option>
          <option value="equals">é igual a</option>
          <option value="not_equals">é diferente de</option>
          <option value="empty">está vazio</option>
          <option value="not_empty">não está vazio</option>
        </select>
      </Field>

      {showValue && (
        <Field label={op === 'contains' ? 'Palavras-chave' : 'Valores aceitos'}>
          <KeywordsInput value={values} onChange={(next) => update({ condition_values: next })} />
        </Field>
      )}

      <p className="rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        💡 Aperte <kbd className="rounded bg-n-0 px-1 font-mono">Enter</kbd> ou{' '}
        <kbd className="rounded bg-n-0 px-1 font-mono">,</kbd> pra adicionar cada palavra. Saída{' '}
        <span className="font-bold text-emerald-600">verdadeiro</span> à esquerda,{' '}
        <span className="font-bold text-rose-600">falso</span> à direita. Comparação é
        case-insensitive.
      </p>
      {source === 'message' && (
        <p className="rounded bg-warning-50 px-3 py-2 text-[11px] text-warning-700">
          ⏸️ Quando comparar com a mensagem, o fluxo <b>pausa</b> e espera a próxima resposta do
          contato antes de avaliar.
        </p>
      )}
    </>
  );
}

const VAR_COMPARATORS: VarComparator[] = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'not_null',
];

/** Gera o `content` (lado esquerdo, que o executor renderiza) a partir do
 *  seletor estruturado — espelha o buildExpression do nó sim/não:
 *  mensagem → `{{message.body}}`; variável → `{{state.<nome>}}`. Variável sem
 *  nome → `''` (igual ao sim/não): evita salvar `{{state.}}` e o card mostra `—`. */
function buildVarsContent(source: ConditionSource, variable: string): string {
  if (source === 'message') return '{{message.body}}';
  if (source === 'atendido_hoje') return TOKEN_ATENDIDO_HOJE;
  const v = variable.trim();
  return v ? `{{state.${v}}}` : '';
}

/** Reconstrói o seletor a partir de um `content` salvo (nós antigos sem
 *  source/variable explícitos): `{{state.X}}` → variável X; senão → mensagem. */
function parseVarsContent(content: string): { source: ConditionSource; variable: string } {
  if (content.includes('ja_atendido_hoje')) return { source: 'atendido_hoje', variable: '' };
  const m = content.match(/\{\{\s*state\.([\w.]+)\s*\}\}/);
  const v = m?.[1];
  return v ? { source: 'variable', variable: v } : { source: 'message', variable: '' };
}

/**
 * Editor do nó "Condição com variáveis" (switch/case multi-ramo). Topo igual ao
 * nó sim/não: seletor "Comparar" (mensagem do contato | variável do fluxo) — o
 * `content` (lado esquerdo) é GERADO a partir dele, sem o usuário digitar
 * `{{...}}`. Abaixo, a lista dinâmica de ramos (comparador + chips de valores).
 * O comparador `not_null` esconde os valores. Cada ramo vira a saída `branch:N`;
 * a saída `default` é o Padrão.
 */
function ConditionVarsEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const content = (cfg.content as string | undefined) ?? '';
  const parsed = parseVarsContent(content);
  const source = (cfg.source as ConditionSource | undefined) ?? parsed.source;
  const variable = (cfg.variable as string | undefined) ?? parsed.variable;
  const branches = (cfg.branches as VarBranch[] | undefined) ?? [];

  // Atualiza fonte/variável e RE-GERA o `content` que o executor lê. O seletor
  // ({{message...}} vs {{state...}}) é o que faz o executor decidir entre
  // "pausa e espera o inbound" (mensagem) e "avalia na hora" (variável).
  const updateSource = (
    changes: Partial<{ source: ConditionSource; variable: string }>,
  ): void => {
    const nextSource = changes.source ?? source;
    const nextVar = changes.variable ?? variable;
    onChange({
      ...cfg,
      source: nextSource,
      variable: nextVar,
      content: buildVarsContent(nextSource, nextVar),
    });
  };

  const updateBranches = (next: VarBranch[]): void => onChange({ ...cfg, branches: next });
  const addBranch = (): void => updateBranches([...branches, { comparator: 'eq', values: [] }]);
  const patchBranch = (idx: number, patch: Partial<VarBranch>): void =>
    updateBranches(branches.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  const removeBranch = (idx: number): void => updateBranches(branches.filter((_, i) => i !== idx));

  const selCls =
    'h-8 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none';
  const tinyLabel = 'mb-1 block text-[10px] text-n-500 uppercase tracking-[.03em]';

  return (
    <>
      <Field label="Comparar">
        <select
          value={source}
          onChange={(e) => updateSource({ source: e.target.value as ConditionSource })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="message">Texto da mensagem do contato</option>
          <option value="variable">Variável do fluxo</option>
          <option value="atendido_hoje">Já foi atendido hoje (sim/não)</option>
        </select>
      </Field>

      {source === 'variable' && (
        <Field label="Nome da variável">
          <TextInput
            value={variable}
            onChange={(v) => updateSource({ variable: v })}
            placeholder="resposta, nome, qualified..."
          />
        </Field>
      )}

      <div className="mt-3">
        <span className={tinyLabel}>Ramos (cada um vira uma saída no nó)</span>
        {branches.map((b, i) => {
          const comparator = b.comparator ?? 'eq';
          const noVal = comparator === 'not_null';
          return (
            <div key={i} className="mb-2 rounded border border-n-200 bg-n-50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-bold text-[11px] text-cyan-700">Ramo {i + 1}</span>
                <button
                  type="button"
                  onClick={() => removeBranch(i)}
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-n-400 hover:bg-danger-100 hover:text-danger-600"
                  aria-label="Remover ramo"
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <span className={tinyLabel}>Comparador</span>
              <select
                value={comparator}
                onChange={(e) =>
                  patchBranch(i, { comparator: e.target.value as VarComparator })
                }
                className={selCls}
                aria-label={`Comparador do ramo ${i + 1}`}
              >
                {VAR_COMPARATORS.map((c) => (
                  <option key={c} value={c}>
                    {VAR_COMPARATOR_LABEL[c]}
                  </option>
                ))}
              </select>
              {!noVal && (
                <>
                  <span className={`${tinyLabel} mt-2`}>Valores</span>
                  <KeywordsInput
                    value={b.values ?? []}
                    onChange={(next) => patchBranch(i, { values: next })}
                    placeholder="valor1, valor2"
                  />
                </>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={addBranch}
          className="flex w-full items-center justify-center gap-2 rounded border border-cyan-400 border-dashed bg-n-0 px-3 py-2 font-semibold text-[12px] text-cyan-700 hover:bg-cyan-50"
        >
          <Plus size={14} /> Adicionar ramo
        </button>
      </div>

      <p className="mt-3 rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        💡 Cada ramo aceita <b>vários valores</b> (aperte{' '}
        <kbd className="rounded bg-n-0 px-1 font-mono">Enter</kbd> ou{' '}
        <kbd className="rounded bg-n-0 px-1 font-mono">,</kbd>) e casa se <b>qualquer um</b> bater.
        Segue pelo <b>primeiro ramo que casar</b> (de cima pra baixo). Nenhum casou → saída{' '}
        <b>Padrão</b>. Comparadores de número (&gt;, ≥, &lt;, ≤) só casam com conteúdo numérico.
      </p>
      {source === 'message' && (
        <p className="rounded bg-warning-50 px-3 py-2 text-[11px] text-warning-700">
          ⏸️ Como compara a mensagem do contato, o fluxo <b>pausa</b> e espera a próxima resposta
          antes de avaliar. Pra variável do fluxo, avalia na hora.
        </p>
      )}
    </>
  );
}

const WEEKDAY_OPTIONS = [
  { v: 0, label: 'Domingo' },
  { v: 1, label: 'Segunda-feira' },
  { v: 2, label: 'Terça-feira' },
  { v: 3, label: 'Quarta-feira' },
  { v: 4, label: 'Quinta-feira' },
  { v: 5, label: 'Sexta-feira' },
  { v: 6, label: 'Sábado' },
];

function MenuEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const prompt = (cfg.prompt as string | undefined) ?? '';
  const variable = (cfg.variable as string | undefined) ?? '';
  const options = (cfg.options as MenuOption[] | undefined) ?? [];

  const updateOptions = (next: MenuOption[]): void => onChange({ ...cfg, options: next });
  const addOption = (): void =>
    updateOptions([...options, { op: 'equals', values: [String(options.length + 1)] }]);
  const patchOption = (idx: number, patch: Partial<MenuOption>): void =>
    updateOptions(options.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  const removeOption = (idx: number): void => updateOptions(options.filter((_, i) => i !== idx));

  // Nome final da variável (espelha o strip do executor): vazio = não salva.
  const varName = variable.replace(/[{}]/g, '').trim();

  const inputCls =
    'h-8 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none';
  const selCls =
    'h-8 flex-shrink-0 rounded border border-n-200 bg-n-0 px-1 text-[12px] focus:border-brand-500 focus:outline-none';
  const tinyLabel = 'mb-1 block text-[10px] text-n-500 uppercase tracking-[.03em]';

  return (
    <>
      <label className="block">
        <span className="mb-1 block font-medium text-[12px] text-n-700">Mensagem (pergunta)</span>
        <textarea
          value={prompt}
          onChange={(e) => onChange({ ...cfg, prompt: e.target.value })}
          rows={4}
          placeholder={'Olá! Selecione uma opção:\n1 - Unidade A\n2 - Unidade B'}
          className="w-full resize-y rounded border border-n-200 bg-n-0 px-2.5 py-2 text-[13px] focus:border-brand-500 focus:outline-none"
        />
        <span className="mt-1 block text-[11px] text-n-500">
          Escreva a lista de opções no próprio texto. Suporta variáveis (ex.{' '}
          <code className="font-mono">{'{{contact.display_name}}'}</code>).
        </span>
      </label>

      <div className="mt-3">
        <span className={tinyLabel}>Opções (cada uma vira uma saída no nó)</span>
        {options.map((o, i) => (
          <div key={i} className="mb-2 rounded border border-n-200 bg-n-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-bold text-[11px] text-indigo-700">Opção {i + 1}</span>
              <button
                type="button"
                onClick={() => removeOption(i)}
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-n-400 hover:bg-danger-100 hover:text-danger-600"
                aria-label="Remover opção"
              >
                <Trash2 size={13} />
              </button>
            </div>
            <span className={tinyLabel}>Condição</span>
            <select
              value={o.op ?? 'equals'}
              onChange={(e) => patchOption(i, { op: e.target.value as MenuOption['op'] })}
              className={`${selCls} w-full`}
              aria-label={`Condição da opção ${i + 1}`}
            >
              <option value="contains">contém</option>
              <option value="equals">é igual a</option>
              <option value="not_equals">é diferente de</option>
              <option value="empty">está vazio</option>
              <option value="not_empty">não está vazio</option>
            </select>
            {o.op !== 'empty' && o.op !== 'not_empty' && (
              <>
                <span className={`${tinyLabel} mt-2`}>Palavras-chave</span>
                <KeywordsInput
                  value={o.values ?? []}
                  onChange={(next) => patchOption(i, { values: next })}
                  placeholder="1, um"
                />
              </>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addOption}
          className="flex w-full items-center justify-center gap-2 rounded border border-indigo-300 border-dashed bg-n-0 px-3 py-2 font-semibold text-[12px] text-indigo-700 hover:bg-indigo-50"
        >
          <Plus size={14} /> Adicionar opção
        </button>
      </div>

      <label className="mt-3 block">
        <span className={tinyLabel}>Salvar resposta em (opcional)</span>
        <input
          value={variable}
          onChange={(e) => onChange({ ...cfg, variable: e.target.value })}
          placeholder="{{opcao}}"
          className={inputCls}
        />
        <span className="mt-1 block text-[11px] text-n-500">
          {varName ? (
            <>
              Salva em <code className="font-mono">{`state.${varName}`}</code> (use{' '}
              <code className="font-mono">{`{{state.${varName}}}`}</code> depois).
            </>
          ) : (
            'Vazio = não salva. Preenchido, guarda a resposta crua do contato.'
          )}
        </span>
      </label>

      <p className="mt-3 rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        💡 Cada opção aceita <b>vários valores</b> (aperte{' '}
        <kbd className="rounded bg-n-0 px-1 font-mono">Enter</kbd> ou{' '}
        <kbd className="rounded bg-n-0 px-1 font-mono">,</kbd>) e casa se <b>qualquer um</b> bater.
        O bot <b>espera a resposta</b> e segue pela <b>primeira opção que casar</b> (de cima pra
        baixo). Nenhuma casou → saída <b>Padrão</b>.
      </p>
    </>
  );
}

function ScheduleTableEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const windows = (cfg.windows as ScheduleWindow[] | undefined) ?? [];

  const update = (next: ScheduleWindow[]): void => onChange({ ...cfg, windows: next });
  const addWindow = (): void =>
    update([
      ...windows,
      { dayStart: 1, dayEnd: 5, startHour: 8, startMin: 0, endHour: 18, endMin: 0 },
    ]);
  const patchWindow = (idx: number, patch: Partial<ScheduleWindow>): void =>
    update(windows.map((w, i) => (i === idx ? { ...w, ...patch } : w)));
  const removeWindow = (idx: number): void => update(windows.filter((_, i) => i !== idx));
  const clamp = (raw: string, max: number): number => {
    const n = Number.parseInt(raw, 10);
    if (Number.isNaN(n)) return 0;
    return Math.max(0, Math.min(max, n));
  };

  const numCls =
    'h-8 w-full rounded border border-n-200 bg-n-0 px-1 text-center text-[12px] focus:border-brand-500 focus:outline-none';
  const selCls =
    'h-8 w-full rounded border border-n-200 bg-n-0 px-1 text-[12px] focus:border-brand-500 focus:outline-none';
  const tinyLabel = 'mb-1 block text-[10px] text-n-500 uppercase tracking-[.03em]';

  return (
    <>
      {windows.map((w, i) => (
        <div key={i} className="rounded border border-n-200 bg-n-50 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="truncate font-bold text-[11px] text-teal-700">
              Faixa {i + 1} · {summarizeScheduleWindow(w)}
            </span>
            <button
              type="button"
              onClick={() => removeWindow(i)}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-n-400 hover:bg-danger-100 hover:text-danger-600"
              aria-label="Remover faixa"
            >
              <Trash2 size={13} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className={tinyLabel}>Dia início</span>
              <select
                value={w.dayStart}
                onChange={(e) => patchWindow(i, { dayStart: Number(e.target.value) })}
                className={selCls}
              >
                {WEEKDAY_OPTIONS.map((d) => (
                  <option key={d.v} value={d.v}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className={tinyLabel}>Dia fim</span>
              <select
                value={w.dayEnd}
                onChange={(e) => patchWindow(i, { dayEnd: Number(e.target.value) })}
                className={selCls}
              >
                {WEEKDAY_OPTIONS.map((d) => (
                  <option key={d.v} value={d.v}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-2">
            <label className="block">
              <span className={tinyLabel}>Hora ini</span>
              <input
                type="number"
                min={0}
                max={23}
                value={w.startHour}
                onChange={(e) => patchWindow(i, { startHour: clamp(e.target.value, 23) })}
                className={numCls}
              />
            </label>
            <label className="block">
              <span className={tinyLabel}>Min ini</span>
              <input
                type="number"
                min={0}
                max={59}
                value={w.startMin}
                onChange={(e) => patchWindow(i, { startMin: clamp(e.target.value, 59) })}
                className={numCls}
              />
            </label>
            <label className="block">
              <span className={tinyLabel}>Hora fim</span>
              <input
                type="number"
                min={0}
                max={23}
                value={w.endHour}
                onChange={(e) => patchWindow(i, { endHour: clamp(e.target.value, 23) })}
                className={numCls}
              />
            </label>
            <label className="block">
              <span className={tinyLabel}>Min fim</span>
              <input
                type="number"
                min={0}
                max={59}
                value={w.endMin}
                onChange={(e) => patchWindow(i, { endMin: clamp(e.target.value, 59) })}
                className={numCls}
              />
            </label>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addWindow}
        className="flex w-full items-center justify-center gap-2 rounded border border-teal-300 border-dashed bg-n-0 px-3 py-2.5 font-semibold text-[12px] text-teal-700 hover:bg-teal-50"
      >
        <Plus size={14} /> Adicionar faixa
      </button>

      <p className="rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        💡 No momento da mensagem, o fluxo segue pela <b>primeira faixa que casar</b> (numeradas 1,
        2, … na base do nó). Se nenhuma casar, segue pela saída <b>Padrão</b>. O horário usa o{' '}
        <b>fuso do tenant</b>.
      </p>
    </>
  );
}

function TransferEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const deptsQ = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => listUsers() });

  const departmentId = (cfg.department_id as number | null | undefined) ?? null;
  const userId = (cfg.user_id as number | null | undefined) ?? null;

  // Atendentes ativos do departamento selecionado, OU todos se nenhum dept
  // selecionado. Admin pode aparecer também — atende como qualquer outro.
  const activeUsers = (usersQ.data ?? []).filter((u) => u.status === 'active');
  const filteredUsers =
    departmentId == null
      ? activeUsers
      : activeUsers.filter((u) => u.departments.some((d) => d.id === departmentId));

  return (
    <>
      <Field label="Departamento">
        <select
          value={departmentId ?? ''}
          onChange={(e) => {
            const id = e.target.value === '' ? null : Number(e.target.value);
            // Trocou o dept: limpa user_id se o user atual não pertence ao
            // novo dept (evita atribuição inválida).
            const u = (usersQ.data ?? []).find((x) => x.id === userId);
            const userStillValid =
              userId == null || id == null || (u?.departments ?? []).some((d) => d.id === id);
            onChange({
              ...cfg,
              department_id: id,
              ...(userStillValid ? {} : { user_id: null }),
            });
          }}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="">— Fila geral —</option>
          {(deptsQ.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Atendente (opcional)">
        <select
          value={userId ?? ''}
          onChange={(e) =>
            onChange({
              ...cfg,
              user_id: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="">— Qualquer atendente da fila —</option>
          {filteredUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </Field>

      <p className="rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        💡 Sem atendente → ticket vai pro modo <b>Pendente</b> e quem aceitar primeiro assume. Com
        atendente → ticket vai direto pra ele como <b>Ativo</b>.
      </p>
    </>
  );
}

function WaitEditor({
  cfg,
  onChange,
}: { cfg: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const mode = (cfg.mode as string | undefined) ?? 'duration';
  const seconds = (cfg.seconds as number | undefined) ?? 60;
  return (
    <>
      <Field label="Modo">
        <select
          value={mode}
          onChange={(e) => onChange({ ...cfg, mode: e.target.value })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        >
          <option value="duration">Duração fixa</option>
          <option value="inbound">Aguardar próximo inbound</option>
        </select>
      </Field>
      {mode === 'duration' && (
        <Field label="Duração (segundos)">
          <input
            type="number"
            min={1}
            max={86400}
            value={seconds}
            onChange={(e) => onChange({ ...cfg, seconds: Number(e.target.value) })}
            className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
          />
        </Field>
      )}
    </>
  );
}

function WebhookEditor({
  cfg,
  onChange,
}: {
  cfg: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const url = (cfg.url as string | undefined) ?? '';
  const timeout = (cfg.timeout_ms as number | undefined) ?? 5000;
  const headers = (cfg.headers as string | undefined) ?? '';
  return (
    <>
      <Field label="URL (https)">
        <TextInput
          value={url}
          onChange={(v) => onChange({ ...cfg, url: v })}
          placeholder="https://api.exemplo.com/hook"
        />
      </Field>
      <Field label="Headers (JSON, opcional)">
        <TextArea
          value={headers}
          onChange={(v) => onChange({ ...cfg, headers: v })}
          placeholder='{"Authorization": "Bearer ..."}'
          rows={3}
        />
      </Field>
      <Field label="Timeout (ms)">
        <input
          type="number"
          min={500}
          max={30000}
          value={timeout}
          onChange={(e) => onChange({ ...cfg, timeout_ms: Number(e.target.value) })}
          className="h-9 w-full rounded border border-n-200 bg-n-0 px-2 text-[12px] focus:border-brand-500 focus:outline-none"
        />
      </Field>
      <p className="rounded bg-n-50 px-3 py-2 text-[11px] text-n-600">
        Resposta JSON é salva em <code className="font-mono">state.webhook_response</code>.
      </p>
    </>
  );
}
